/**
 * Column outline groups, as Google Sheets' Data > Group columns: a run of
 * adjacent columns that folds behind a +/− button. Unlike Sheets, where the
 * button side is one switch for the whole sheet, each group keeps its own side.
 *
 * A group is stored by its columns' stable keys and laid out by where those
 * keys sit in the column order, so it survives renames and reorders. Groups
 * nest by `level`: groups on one level never overlap, and a group on level n+1
 * lies inside one on level n.
 */

export type ColumnGroupSide = 'left' | 'right'

export interface ColumnGroup {
  keys: string[]
  level: number
  collapsed?: boolean
  /** Where the +/− button sits: over the column just left or just right of the group. */
  side?: ColumnGroupSide
}

export interface ColumnGroupSpan {
  /** Index into the stored list. */
  id: number
  level: number
  /** Positions in the full column order, inclusive. */
  start: number
  end: number
  collapsed: boolean
  side: ColumnGroupSide
}

/** Each group's span in `order` (every column key, hidden ones too); groups with no known column drop out. */
export function columnGroupSpans(groups: readonly ColumnGroup[], order: readonly string[]): ColumnGroupSpan[] {
  const pos = new Map(order.map((key, i) => [key, i]))
  return groups.flatMap((group, id) => {
    const positions = group.keys.map((key) => pos.get(key)).filter((p): p is number => p !== undefined)
    if (positions.length === 0) return []
    return [{ id, level: group.level, start: Math.min(...positions), end: Math.max(...positions), collapsed: Boolean(group.collapsed), side: group.side ?? 'left' }]
  })
}

const keysOf = (order: readonly string[], start: number, end: number) => order.slice(start, end + 1)

/**
 * Groups `start..end` (positions in `order`). It goes on the shallowest level
 * where it overlaps nothing; a group equal to or containing it pushes it a level
 * deeper, and one it only partly overlaps is merged into it.
 */
export function addColumnGroup(groups: readonly ColumnGroup[], order: readonly string[], start: number, end: number): ColumnGroup[] {
  const spans = columnGroupSpans(groups, order)
  for (let level = 0; ; level++) {
    const hits = spans.filter((s) => s.level === level && s.start <= end && s.end >= start)
    if (hits.length === 0) {
      return [...groups.map((g) => ({ ...g })), { keys: keysOf(order, start, end), level, side: 'left' }]
    }
    const container = hits.length === 1 && hits[0]!.start <= start && hits[0]!.end >= end ? hits[0] : undefined
    if (container) continue
    const from = Math.min(start, ...hits.map((s) => s.start))
    const to = Math.max(end, ...hits.map((s) => s.end))
    const merged = new Set(hits.map((s) => s.id))
    const first = hits[0]!
    return [
      ...groups.filter((_, id) => !merged.has(id)).map((g) => ({ ...g })),
      { keys: keysOf(order, from, to), level, side: first.side, ...(first.collapsed ? { collapsed: true } : {}) },
    ]
  }
}

/**
 * Takes `start..end` out of the deepest groups it touches, as Sheets does: a
 * group cut in the middle splits in two, one emptied disappears, and the levels
 * below it close up.
 */
export function removeColumnGroup(groups: readonly ColumnGroup[], order: readonly string[], start: number, end: number): ColumnGroup[] {
  const spans = columnGroupSpans(groups, order)
  const hits = spans.filter((s) => s.start <= end && s.end >= start)
  if (hits.length === 0) return groups.map((g) => ({ ...g }))
  const deepest = Math.max(...hits.map((s) => s.level))
  const cut = new Set(hits.filter((s) => s.level === deepest).map((s) => s.id))
  const result: ColumnGroup[] = []
  groups.forEach((group, id) => {
    const span = spans.find((s) => s.id === id)
    if (!cut.has(id) || !span) { result.push({ ...group }); return }
    const { keys: _keys, ...rest } = group
    if (span.start < start) result.push({ ...rest, keys: keysOf(order, span.start, start - 1) })
    if (span.end > end) result.push({ ...rest, keys: keysOf(order, end + 1, span.end) })
  })
  return compactLevels(result, order)
}

/** Drops empty levels so the outline never shows a blank row. */
function compactLevels(groups: ColumnGroup[], order: readonly string[]): ColumnGroup[] {
  const used = [...new Set(columnGroupSpans(groups, order).map((s) => s.level))].sort((a, b) => a - b)
  const remap = new Map(used.map((level, i) => [level, i]))
  return groups.filter((g) => remap.has(g.level)).map((g) => ({ ...g, level: remap.get(g.level)! }))
}

/** The keys a collapsed group hides. */
export function collapsedColumnKeys(groups: readonly ColumnGroup[], order: readonly string[]): Set<string> {
  const hidden = new Set<string>()
  for (const span of columnGroupSpans(groups, order)) {
    if (span.collapsed) keysOf(order, span.start, span.end).forEach((key) => hidden.add(key))
  }
  return hidden
}

export function updateColumnGroup(groups: readonly ColumnGroup[], id: number, patch: Partial<ColumnGroup>): ColumnGroup[] {
  return groups.map((g, i) => (i === id ? { ...g, ...patch } : { ...g }))
}
